import { useEffect, useRef, useCallback } from "react"

interface TurnstileWidgetProps {
  siteKey: string
  onVerify: (token: string) => void
}

export function TurnstileWidget({ siteKey, onVerify }: TurnstileWidgetProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onVerifyRef = useRef(onVerify)
  onVerifyRef.current = onVerify

  const renderWidget = useCallback(() => {
    if (
      typeof window === "undefined" ||
      !(window as any).turnstile ||
      !containerRef.current
    )
      return

    if (widgetIdRef.current !== null) {
      try {
        ;(window as any).turnstile.remove(widgetIdRef.current)
      } catch {
        // Widget may already be removed
      }
      widgetIdRef.current = null
    }

    widgetIdRef.current = (window as any).turnstile.render(
      containerRef.current,
      {
        sitekey: siteKey,
        theme: "light",
        callback: (token: string) => onVerifyRef.current(token),
      }
    )
  }, [siteKey])

  useEffect(() => {
    if (typeof window === "undefined") return

    if ((window as any).turnstile) {
      renderWidget()
    } else {
      const existingScript = document.querySelector(
        'script[src*="challenges.cloudflare.com/turnstile"]'
      )

      if (existingScript) {
        const interval = setInterval(() => {
          if ((window as any).turnstile) {
            clearInterval(interval)
            renderWidget()
          }
        }, 100)
        return () => clearInterval(interval)
      }

      const callbackName = `onTurnstileLoad_${Date.now()}`
      ;(window as any)[callbackName] = () => {
        renderWidget()
        delete (window as any)[callbackName]
      }

      const script = document.createElement("script")
      script.src = `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${callbackName}`
      script.async = true
      document.head.appendChild(script)
    }

    return () => {
      if (widgetIdRef.current !== null && (window as any).turnstile) {
        try {
          ;(window as any).turnstile.remove(widgetIdRef.current)
        } catch {
          // Widget may already be removed
        }
        widgetIdRef.current = null
      }
    }
  }, [renderWidget])

  return <div ref={containerRef} />
}
